import { notifications } from "@mantine/notifications";
import { createFileRoute } from "@tanstack/react-router";
import {
	lazy,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { buildGraphSaveRequest } from "../api/graphSavePayload";
import type { paths } from "../api/types.gen";
import { EdgeDetailDrawer } from "../components/EdgeDetailDrawer";
import { FloatingAddButton } from "../components/FloatingAddButton";
import { FloatingAnimationControls } from "../components/FloatingAnimationControls";
import { FloatingExitFullscreen } from "../components/FloatingExitFullscreen";
import { FloatingLegend } from "../components/FloatingLegend";
import { FloatingPathFinder } from "../components/FloatingPathFinder";
import { FloatingSettings } from "../components/FloatingSettings";
import {
	buildEditNode,
	Graph,
	type GraphRef,
} from "../components/GlobalViews/Graph";
import {
	GraphLoadingOverlay,
	type GraphLoadProgress,
	GraphSyncIndicator,
	useGraphLoadProgress,
} from "../components/GraphLoading";
import { CommandBar } from "../components/Layout/CommandBar";
import { ContextPanel } from "../components/Layout/ContextPanel";
import { NavigationSidebar } from "../components/Layout/NavigationSidebar";
import { ViewportManager } from "../components/Layout/ViewportManager";
import { WorkspaceLayout } from "../components/Layout/WorkspaceLayout";
import { LoadingState } from "../components/LoadingState";
import { LocalViewContainer } from "../components/LocalViews/LocalViewContainer";
import { NodeDetailSidebar } from "../components/NodeDetailSidebar";
import { buildNodeHoverResolver } from "../components/NodeHoverCard";
import { FloatingSavaButton, SavaSidebar } from "../components/Sava";
import { ViewLoader } from "../components/ViewLoader";
import {
	CsvWorkflow,
	FloatingWorkflowsButton,
	ImageWorkflow,
	LinkedInWorkflow,
	ManualWorkflow,
	MarkdownWorkflow,
	NaturalLanguageWorkflow,
	VCardWorkflow,
	WorkflowProvider,
	WorkflowsSidebar,
} from "../components/Workflows";
import { useWorkflowOptional } from "../components/Workflows/context/WorkflowContext";
import type { WorkflowSaveHandler } from "../components/Workflows/types";
import {
	GLOBAL_VIEW_TYPES,
	LOCAL_VIEW_TYPES,
	VIEW_MODES,
} from "../constants/viewModes";
import {
	type NodeLinkContextValue,
	NodeLinkProvider,
} from "../contexts/NodeLinkContext";
import { SavaProvider, useSava } from "../contexts/SavaContext";
import { useSearch } from "../contexts/SearchContext";
import { useViewState } from "../contexts/ViewStateContext";
import {
	BetaAccessStatus,
	type GetCurrentUserAndOrganizationsQuery,
	useGetCurrentUserAndOrganizationsQuery,
} from "../graphql/generated/types.gen";
import {
	type ActiveOrganization,
	useActiveOrganization,
} from "../hooks/useActiveOrganization";
import { useAuthCompat } from "../hooks/useAuthCompat";
import { useBfsAnimation } from "../hooks/useBfsAnimation";
import { useFilteredGraphView } from "../hooks/useFilteredGraphView";
import { useFullscreen } from "../hooks/useFullscreen";
import { useGraphApi } from "../hooks/useGraphApi";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { type TracedGraphPath, usePathTrace } from "../hooks/usePathTrace";
import type {
	ExistingNode,
	NodeUpdateItem,
	PropertyValue,
} from "../types/graph";
import type { MutatedEntityIds, SavaReadResult } from "../types/natural-query";
import { GRAPH_DATA_CHANGED_EVENT } from "../utils/graphDataEvents";
import { LINKEDIN_PROPERTY, linkedInSlug } from "../utils/linkedinProfile";

// Lazy view boundaries: ListView pulls @tanstack/table-core and DuplicatesView
// its matching UI — neither renders at first paint (Graph is the default
// view), so they load on first switch instead of inflating the boot chunk.
const DuplicatesView = lazy(() =>
	import("../components/GlobalViews/DuplicatesView").then((m) => ({
		default: m.DuplicatesView,
	})),
);
const ListView = lazy(() =>
	import("../components/GlobalViews/ListView").then((m) => ({
		default: m.ListView,
	})),
);

type GraphData =
	paths["/v1/graph/data/query"]["post"]["responses"]["200"]["content"]["application/json"];

// Stable fixture the loading overlay shows from the viewport's first paint,
// before the fetch effect has fired its real "connecting" signal.
const INITIAL_CONNECTING: GraphLoadProgress = {
	stage: "connecting",
	stageFraction: 0,
};

// Outcome of a full graph refetch. `superseded` means a newer fetch is already
// in flight and owns the UI update; `error` means the request failed and the
// rendered graph is now stale with nothing else coming.
type GraphFetchResult =
	| { status: "ok"; data: GraphData }
	| { status: "superseded" }
	| { status: "error" };

export const Route = createFileRoute("/")({
	component: Index,
});

function Index() {
	const { user, isLoading } = useAuthCompat();
	const { data: userData } = useGetCurrentUserAndOrganizationsQuery({
		skip: !user,
	});
	const { activeOrganization, isReady } = useActiveOrganization();

	// Handle redirects based on authentication and user state
	useEffect(() => {
		if (isLoading) return;

		// Redirect to welcome if not authenticated
		if (!user || !userData?.currentUser) {
			window.location.href = "/welcome";
			return;
		}

		const isWaitlisted =
			userData.currentUser.betaAccessStatus === BetaAccessStatus.Waitlist;

		// Redirect to waitlist if user is not approved
		if (isWaitlisted) {
			window.location.href = "/waitlist";
			return;
		}

		// Redirect to organization setup if user has no organizations
		if (!userData.organizations?.length) {
			window.location.href = "/organization-setup";
		}
	}, [user, userData, isLoading]);

	// Show loading state while data is being fetched
	if (!isReady) {
		// Handle redirects first, then show loading
		if (!isLoading && (!user || !userData?.currentUser)) {
			return null; // Redirect will happen in useEffect
		}

		if (
			!isLoading &&
			userData?.currentUser &&
			!userData?.organizations?.length
		) {
			return null; // Redirect will happen in useEffect
		}

		return <LoadingState />;
	}

	// At this point, activeOrganization is guaranteed to be non-null
	return (
		<WorkflowProvider organizationId={activeOrganization?.id}>
			<SavaProvider>
				<IndexContent
					activeOrganization={activeOrganization as ActiveOrganization}
					userData={userData}
				/>
			</SavaProvider>
		</WorkflowProvider>
	);
}

interface IndexContentProps {
	activeOrganization: ActiveOrganization;
	userData: GetCurrentUserAndOrganizationsQuery | undefined;
}

function IndexContent({ activeOrganization, userData }: IndexContentProps) {
	const {
		fetchAllGraphData,
		submitGraphDataChunked,
		bulkGraphOperations,
		getWriteError,
	} = useGraphApi();
	const { setOnNodeSelected } = useSearch();
	const viewState = useViewState();
	const sava = useSava();
	const workflow = useWorkflowOptional();

	// Mutual sidebar exclusion: when Sava opens, close Workflows
	useEffect(() => {
		if (sava.sidebarOpen && workflow?.sidebarOpen) {
			workflow.setSidebarOpen(false);
		}
	}, [sava.sidebarOpen, workflow?.sidebarOpen, workflow?.setSidebarOpen]);

	// When Workflows opens, close Sava
	useEffect(() => {
		if (workflow?.sidebarOpen && sava.sidebarOpen) {
			sava.setSidebarOpen(false);
		}
	}, [workflow?.sidebarOpen, sava.sidebarOpen, sava.setSidebarOpen]);

	// Fullscreen mode for graph viewport
	const { isFullscreen, toggleFullscreen } = useFullscreen();
	const viewportRef = useRef<HTMLDivElement>(null);

	// BFS animation for fullscreen 2D mode
	const bfsAnimation = useBfsAnimation();
	const [animationStartNodeId, setAnimationStartNodeId] = useState<
		string | null
	>(null);

	// Enable keyboard shortcuts
	const handleToggleFullscreen = useCallback(() => {
		toggleFullscreen(viewportRef.current);
	}, [toggleFullscreen]);
	const handleToggleSava = useCallback(() => {
		sava.setSidebarOpen(!sava.sidebarOpen);
	}, [sava]);
	useKeyboardShortcuts({
		onToggleFullscreen: handleToggleFullscreen,
		onToggleSava: handleToggleSava,
	});

	// At this point, we can safely assume activeOrganization exists
	const [graphData, setGraphData] = useState<GraphData | null>(null);

	// Apply filters: `filteredGraphData` is the materialized subset used by list
	// and detail views; `hiddenNodeIds`/`hiddenEdgeIds` drive the <Graph>
	// reducer so filter changes don't touch the force-layout pipeline.
	const {
		data: filteredGraphData,
		hiddenNodeIds,
		hiddenEdgeIds,
	} = useFilteredGraphView(graphData);

	const trace = usePathTrace();
	const graphRef = useRef<GraphRef>(null);

	// Monotonic guard: concurrent full fetches (e.g. Sava's debounced live
	// refresh vs its on-complete refetch) can resolve out of order; a stale
	// resolution must not clobber newer data — applyDataDiff would drop the
	// nodes the newer fetch just added.
	const fetchSeqRef = useRef(0);

	// Loading experience state machine. `graphDataRef` lets the fetch callback
	// classify itself (initial = blank canvas → full overlay; background =
	// graph already on screen → corner sync pill) without widening its deps.
	const {
		progress: graphLoadProgress,
		isBackgroundRefreshing,
		handlers: graphLoadHandlers,
	} = useGraphLoadProgress();
	const graphDataRef = useRef<GraphData | null>(null);
	useEffect(() => {
		graphDataRef.current = graphData;
	}, [graphData]);

	// A load is always imminent when the workspace has no graph data (mount and
	// org switch both fetch in an effect), so prime the overlay immediately
	// rather than letting the canvas sit bare until onFetchStart fires.
	const displayedGraphLoadProgress =
		graphLoadProgress ?? (graphData === null ? INITIAL_CONNECTING : null);

	const handleFetchGraphData =
		useCallback(async (): Promise<GraphFetchResult> => {
			const seq = ++fetchSeqRef.current;
			graphLoadHandlers.onFetchStart(
				graphDataRef.current === null ? "initial" : "background",
			);
			const data = await fetchAllGraphData(activeOrganization.id, {
				// A superseded fetch must not narrate over the one that owns the UI.
				onDownloadProgress: (received, total) => {
					if (seq === fetchSeqRef.current) {
						graphLoadHandlers.onDownloadProgress(received, total);
					}
				},
			});
			// A newer fetch superseded this one — it owns the UI update; do nothing.
			if (seq !== fetchSeqRef.current) return { status: "superseded" };
			// fetchAllGraphData returns null only on a caught request error.
			if (!data) {
				graphLoadHandlers.onFetchError();
				return { status: "error" };
			}
			// Complete + setGraphData batch into one render, so the "Weaving the
			// graph" frame paints before the synchronous applyDataDiff effect
			// blocks the main thread.
			graphLoadHandlers.onFetchComplete({
				nodeCount: data.nodes.length,
				edgeCount: data.edges.length,
			});
			setGraphData(data);
			return { status: "ok", data };
		}, [activeOrganization.id, fetchAllGraphData, graphLoadHandlers]);

	// Convert graph nodes to ExistingNode format for workflow components
	const existingNodes: ExistingNode[] = useMemo(() => {
		if (!graphData?.nodes) return [];
		return graphData.nodes.map((node) => ({
			id: node.id,
			displayName: node.displayName,
			type: node.typeName || "Entity",
			linkedInSlug: linkedInSlug(node.properties?.[LINKEDIN_PROPERTY]),
			properties: node.properties as Record<string, PropertyValue> | undefined,
		}));
	}, [graphData?.nodes]);

	// Pairs that already have a relationship, so an import can report which of
	// its edges are genuinely new rather than a count of everything it sends.
	const existingEdgePairs: Set<string> = useMemo(() => {
		const pairs = new Set<string>();
		for (const edge of graphData?.edges ?? []) {
			pairs.add(`${edge.sourceNode}|${edge.targetNode}`);
		}
		return pairs;
	}, [graphData?.edges]);

	// Handle saving nodes and edges from workflow. Large imports (e.g. LinkedIn)
	// pass onProgress and are saved in resilient chunks; the thrown message is the
	// real, status-classified reason so the UI can show it instead of a generic one.
	const handleWorkflowSave = useCallback<WorkflowSaveHandler>(
		async (nodes, edges, options) => {
			const { events = [], onProgress } = options ?? {};

			const response = await submitGraphDataChunked(
				buildGraphSaveRequest(activeOrganization.id, nodes, edges, events),
				{ onProgress },
			);

			if (!response) {
				throw new Error(getWriteError() ?? "Failed to save graph data");
			}
		},
		[activeOrganization.id, submitGraphDataChunked, getWriteError],
	);

	// Handle updating existing nodes (e.g., user's node with skills)
	const handleUpdateNodes = useCallback(
		async (nodes: NodeUpdateItem[]) => {
			const nodesToUpdate = nodes.map((node) => ({
				id: node.id,
				displayName: node.displayName,
				typeName: node.typeName,
				properties: node.properties,
				propertiesMode: node.propertiesMode,
			}));

			const response = await bulkGraphOperations({
				organizationId: activeOrganization.id,
				nodesToUpdate,
			});

			if (!response) {
				throw new Error("Failed to update nodes");
			}
		},
		[activeOrganization.id, bulkGraphOperations],
	);

	// Find user's node ID for animation and pathfinding
	const userNodeId = useMemo(() => {
		if (!graphData?.nodes || !userData?.currentUser?.displayName) return null;
		const userNode = graphData.nodes.find(
			(node) =>
				node.displayName === userData.currentUser?.displayName &&
				node.typeName?.toLowerCase() === "person",
		);
		return userNode?.id ?? activeOrganization.nodeId ?? null;
	}, [
		graphData?.nodes,
		userData?.currentUser?.displayName,
		activeOrganization.nodeId,
	]);

	// Find user's node by matching displayName and set as default "from" node
	useEffect(() => {
		if (
			graphData?.nodes &&
			userData?.currentUser?.displayName &&
			trace.selection.from === null
		) {
			const userNode = graphData.nodes.find(
				(node) =>
					node.displayName === userData.currentUser?.displayName &&
					node.typeName?.toLowerCase() === "person",
			);

			if (userNode) {
				trace.setFrom(userNode.id);
			}
		}
	}, [
		graphData?.nodes,
		userData?.currentUser?.displayName,
		trace.selection.from,
		trace.setFrom,
	]);

	// Trigger BFS animation when entering fullscreen in 2D mode
	const prevIsFullscreen = useRef(false);
	useEffect(() => {
		const justEnteredFullscreen = isFullscreen && !prevIsFullscreen.current;
		prevIsFullscreen.current = isFullscreen;

		if (
			justEnteredFullscreen &&
			viewState.globalViewType === GLOBAL_VIEW_TYPES["2D"] &&
			viewState.fullscreenAnimationEnabled &&
			filteredGraphData?.nodes &&
			filteredGraphData?.edges &&
			filteredGraphData.nodes.length > 0
		) {
			// Use user's node if available, otherwise pick the most connected node
			let startNodeId = userNodeId;
			if (!startNodeId) {
				// Count connections per node
				const connectionCount = new Map<string, number>();
				for (const edge of filteredGraphData.edges) {
					connectionCount.set(
						edge.sourceNode,
						(connectionCount.get(edge.sourceNode) || 0) + 1,
					);
					connectionCount.set(
						edge.targetNode,
						(connectionCount.get(edge.targetNode) || 0) + 1,
					);
				}
				// Find node with most connections
				let maxConnections = -1;
				for (const node of filteredGraphData.nodes) {
					const count = connectionCount.get(node.id) || 0;
					if (count > maxConnections) {
						maxConnections = count;
						startNodeId = node.id;
					}
				}
				// Fallback to first node if no edges
				if (!startNodeId) {
					startNodeId = filteredGraphData.nodes[0].id;
				}
			}

			setAnimationStartNodeId(startNodeId);
			bfsAnimation.startAnimation(
				filteredGraphData.nodes,
				filteredGraphData.edges,
				startNodeId,
			);
		} else if (!isFullscreen && bfsAnimation.state.isAnimating) {
			setAnimationStartNodeId(null);
			bfsAnimation.resetAnimation();
		}
	}, [
		isFullscreen,
		viewState.globalViewType,
		viewState.fullscreenAnimationEnabled,
		filteredGraphData?.nodes,
		filteredGraphData?.edges,
		userNodeId,
		bfsAnimation,
	]);

	// Keyboard shortcuts for animation (Escape/Space to skip)
	useEffect(() => {
		if (!isFullscreen || !bfsAnimation.state.isAnimating) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape" || e.key === " ") {
				e.preventDefault();
				bfsAnimation.skipAnimation();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [isFullscreen, bfsAnimation.state.isAnimating, bfsAnimation]);

	// Org switch: drop the previous org's graph immediately (it used to linger
	// on screen for the whole refetch) and rewind the loading state machine.
	// Declared BEFORE the fetch effect so it runs first in the same commit;
	// the synchronous ref write makes that fetch classify itself as an
	// initial load (full overlay) rather than a background refresh. The
	// previous-id guard skips the redundant first-mount run.
	const loadedOrgIdRef = useRef(activeOrganization.id);
	useEffect(() => {
		if (loadedOrgIdRef.current === activeOrganization.id) return;
		loadedOrgIdRef.current = activeOrganization.id;
		setGraphData(null);
		graphDataRef.current = null;
		graphLoadHandlers.reset();
	}, [activeOrganization.id, graphLoadHandlers]);

	// The single mount + org-change fetch. handleFetchGraphData's identity
	// changes with activeOrganization.id, so this covers initial load AND org
	// switches — a separate `if (!graphData)` mount effect here previously
	// fired a duplicate 1.8MB request in the same commit.
	useEffect(() => {
		handleFetchGraphData();
	}, [handleFetchGraphData]);

	// Refresh graph when data is imported via DataManagement
	useEffect(() => {
		const handler = () => handleFetchGraphData();
		window.addEventListener(GRAPH_DATA_CHANGED_EVENT, handler);
		return () => window.removeEventListener(GRAPH_DATA_CHANGED_EVENT, handler);
	}, [handleFetchGraphData]);

	const handlePathsFound = useCallback(
		(paths: TracedGraphPath[]) => {
			trace.trace(paths);
		},
		[trace.trace],
	);

	const handleNodeSelectionChange = useCallback(
		(fromNode: string | null, toNode: string | null) => {
			trace.setSelection({ from: fromNode, to: toNode });
		},
		[trace.setSelection],
	);

	const handleSetAsSource = trace.setFrom;
	const handleSetAsTarget = trace.setTo;

	const handleCenterAtNode = useCallback(
		(nodeId: string) => {
			if (
				viewState.globalViewType === GLOBAL_VIEW_TYPES["2D"] &&
				graphRef.current
			) {
				graphRef.current.focusNode(nodeId);
			}
		},
		[viewState.globalViewType],
	);

	const [pendingFocusNodeIds, setPendingFocusNodeIds] = useState<
		string[] | null
	>(null);

	// After a read-only Sava turn. Routes from find_introduction_path drive the
	// path tool itself — endpoints, traced route, alternatives — and stay until
	// the user clears them, exactly as if traced by hand. Anything else is a
	// transient emphasis that leaves a hand-traced route intact.
	const handleSavaReadResult = useCallback(
		({ highlightNodeIds, paths }: SavaReadResult) => {
			if (paths.length > 0) {
				trace.trace(
					paths.map((p) => ({
						nodes: p.nodeIds,
						edges: p.edges,
						totalWeight: p.totalStrength,
					})),
				);
				setPendingFocusNodeIds(paths[0].nodeIds);
				return;
			}
			trace.flash(
				highlightNodeIds.length > 0
					? { nodes: highlightNodeIds, edges: [] }
					: null,
			);
		},
		[trace.trace, trace.flash],
	);

	// After a Sava turn that wrote to the graph: refetch, then highlight the
	// created/updated entities and queue a camera focus on them.
	const handleSavaMutation = useCallback(
		async (mutated: MutatedEntityIds) => {
			const result = await handleFetchGraphData();
			// The write succeeded server-side but the local graph is now stale and
			// no newer fetch is coming — tell the user rather than failing silently.
			if (result.status === "error") {
				notifications.show({
					title: "Graph updated",
					message: "Sava changed the graph, but refreshing the view failed.",
					color: "orange",
				});
				return;
			}
			if (result.status !== "ok") return;
			const data = result.data;

			const edgeIdSet = new Set([
				...mutated.created.edgeIds,
				...mutated.updated.edgeIds,
			]);
			const edges = data.edges
				.filter((e) => edgeIdSet.has(e.id))
				.map((e) => ({ id: e.id, source: e.sourceNode, target: e.targetNode }));

			// Restrict to nodes that survived the turn (created-then-deleted ids
			// appear in both sets), and pull in edge endpoints: the path reducer
			// dims every non-path node, so a highlighted edge between two dimmed
			// nodes reads as broken.
			const liveNodeIds = new Set(data.nodes.map((n) => n.id));
			const nodeIds = new Set(
				[...mutated.created.nodeIds, ...mutated.updated.nodeIds].filter((id) =>
					liveNodeIds.has(id),
				),
			);
			for (const e of edges) {
				nodeIds.add(e.source);
				nodeIds.add(e.target);
			}
			// Deletion-only turn: the refetch already removed the entities and
			// the camera deliberately stays put.
			if (nodeIds.size === 0) return;

			trace.flash({ nodes: [...nodeIds], edges });
			setPendingFocusNodeIds([...nodeIds]);
		},
		[handleFetchGraphData, trace.flash],
	);

	// Focus after the refetched data has committed: the Graph child's data
	// effect (applyDataDiff + synchronous sigma.refresh) runs before this
	// parent effect, so the nodes are guaranteed to exist in Graphology.
	useEffect(() => {
		if (!pendingFocusNodeIds) return;
		setPendingFocusNodeIds(null);
		// Graph is unmounted in LOCAL view mode (and for LIST/DUPLICATES even
		// when globalViewType is "2d") — the camera focus and the filter toast
		// only make sense over a live canvas. The highlight state still applies
		// if the user returns within its auto-clear window.
		const graph = graphRef.current;
		if (viewState.globalViewType !== GLOBAL_VIEW_TYPES["2D"] || !graph) return;
		const visible = pendingFocusNodeIds.filter((id) => !hiddenNodeIds.has(id));
		if (visible.length === 0) {
			notifications.show({
				title: "Changes hidden by filters",
				message: "Sava updated entities that are hidden by the active filters.",
				color: "violet",
			});
			return;
		}
		graph.focusNodes(visible);
	}, [pendingFocusNodeIds, viewState.globalViewType, hiddenNodeIds]);

	// The node detail panel is hosted here rather than inside <Graph>, because
	// Sava's node links open it from list and duplicates views too — where the
	// graph is unmounted. Which node is open lives in view state so the edge
	// timeline can be pushed over it and closed back to it from anywhere.
	const editNode = useMemo(
		() =>
			viewState.detailNodeId && graphData
				? buildEditNode(graphData, viewState.detailNodeId)
				: null,
		[viewState.detailNodeId, graphData],
	);
	const { resetDetailPanel } = viewState;

	// Leaving the route entirely closes the panel: a target left behind would
	// keep the standalone edge drawer suppressed with nothing rendering it.
	useEffect(() => resetDetailPanel, [resetDetailPanel]);

	// Hover-card data for inline node references comes from the loaded graph, so
	// counts and properties match what the canvas shows. Rebuilt per refetch —
	// it indexes every node and edge once, rather than per hover.
	const resolveNode = useMemo(
		() =>
			buildNodeHoverResolver(graphData?.nodes ?? [], graphData?.edges ?? []),
		[graphData],
	);

	// Clicking a Sava node link opens the entity's panel and, when a canvas is
	// on screen, centers it there too.
	const activateNode = useCallback(
		(nodeId: string) => {
			if (!resolveNode(nodeId)) {
				notifications.show({
					title: "Entity not loaded",
					message:
						"That entity isn't in the graph currently loaded. Try refreshing.",
					color: "violet",
				});
				return;
			}
			viewState.openNodePanel(nodeId);
			const graph = graphRef.current;
			if (!graph) return;
			if (hiddenNodeIds.has(nodeId)) {
				notifications.show({
					title: "Hidden by filters",
					message: "The entity's details are open, but filters hide it here.",
					color: "violet",
				});
				return;
			}
			graph.focusNode(nodeId);
		},
		[resolveNode, viewState.openNodePanel, hiddenNodeIds],
	);

	const nodeLinks = useMemo<NodeLinkContextValue>(
		() => ({
			resolveNode,
			activateNode,
			findPathFrom: handleSetAsSource,
			findPathTo: handleSetAsTarget,
		}),
		[resolveNode, activateNode, handleSetAsSource, handleSetAsTarget],
	);

	// Register Sava read-result callback so query results trace or highlight
	useEffect(() => {
		sava.registerReadResultCallback(handleSavaReadResult);
	}, [sava.registerReadResultCallback, handleSavaReadResult]);

	// Register Sava mutation callback so write tools refresh + spotlight the graph
	useEffect(() => {
		sava.registerMutationCallback(handleSavaMutation);
	}, [sava.registerMutationCallback, handleSavaMutation]);

	const handleNodeClickInList = useCallback(
		(nodeId: string) => {
			viewState.setFocusedNodeId(nodeId);
			viewState.setLocalViewType(LOCAL_VIEW_TYPES.DETAIL);
			viewState.setViewMode(VIEW_MODES.LOCAL);
		},
		[
			viewState.setFocusedNodeId,
			viewState.setLocalViewType,
			viewState.setViewMode,
		],
	);

	// Register the centering function with the search context
	useEffect(() => {
		setOnNodeSelected(handleCenterAtNode);
	}, [handleCenterAtNode, setOnNodeSelected]);

	// Render global view based on current type - memoized for performance
	// biome-ignore lint/correctness/useExhaustiveDependencies: renderTick intentionally triggers re-renders during animation
	const globalView = useMemo(
		() => (
			<div style={{ width: "100%", height: "100%" }}>
				{/* Suspense covers the lazy ListView/DuplicatesView chunks; Graph is
				    statically imported and never suspends. */}
				<Suspense fallback={<ViewLoader />}>
					{viewState.globalViewType === GLOBAL_VIEW_TYPES.DUPLICATES ? (
						<DuplicatesView organizationId={activeOrganization.id} />
					) : viewState.globalViewType === GLOBAL_VIEW_TYPES["2D"] ? (
						<Graph
							ref={graphRef}
							data={graphData}
							hiddenNodeIds={hiddenNodeIds}
							hiddenEdgeIds={hiddenEdgeIds}
							showLabels={viewState.showLabels}
							highlightedPath={trace.highlightedPath}
							selection={trace.selection}
							onSelect={(slot, nodeId) => {
								if (slot === "from") handleSetAsSource(nodeId);
								else handleSetAsTarget(nodeId);
							}}
							onRefresh={handleFetchGraphData}
							onLayoutProgress={graphLoadHandlers.onLayoutProgress}
							onLayoutSettled={graphLoadHandlers.onLayoutSettled}
							bfsAnimation={
								isFullscreen && animationStartNodeId ? bfsAnimation : null
							}
						/>
					) : viewState.globalViewType === GLOBAL_VIEW_TYPES.LIST ? (
						<ListView
							// biome-ignore lint/suspicious/noExplicitAny: graph library uses any
							data={filteredGraphData as any}
							organizationId={activeOrganization.id}
							onNodeClick={handleNodeClickInList}
							onRefresh={handleFetchGraphData}
						/>
					) : (
						<div className="flex items-center justify-center w-full h-full text-gray-500">
							Unknown view type: {viewState.globalViewType}
						</div>
					)}
				</Suspense>
			</div>
		),
		[
			viewState.globalViewType,
			viewState.showLabels,
			graphData,
			filteredGraphData,
			hiddenNodeIds,
			hiddenEdgeIds,
			trace.highlightedPath,
			trace.selection,
			handleSetAsSource,
			handleSetAsTarget,
			handleFetchGraphData,
			handleNodeClickInList,
			graphLoadHandlers.onLayoutProgress,
			graphLoadHandlers.onLayoutSettled,
			activeOrganization.id,
			isFullscreen,
			animationStartNodeId,
			bfsAnimation.state.isAnimating,
			bfsAnimation.state.isComplete,
			bfsAnimation.state.progress,
			bfsAnimation.state.renderTick,
			bfsAnimation.getNodeOpacity,
		],
	);

	// Render local view based on current type - memoized for performance
	const localView = useMemo(
		() => (
			<LocalViewContainer
				graphData={filteredGraphData}
				onRefresh={handleFetchGraphData}
			/>
		),
		[filteredGraphData, handleFetchGraphData],
	);

	return (
		<NodeLinkProvider value={nodeLinks}>
			{/* Command Bar */}
			<CommandBar onNodeSelected={handleCenterAtNode} />

			{/* Workspace Layout */}
			<WorkspaceLayout
				navigationSidebar={<NavigationSidebar />}
				mainCanvas={
					<div
						ref={viewportRef}
						style={{ width: "100%", height: "100%", position: "relative" }}
					>
						{/* ViewportManager handles view mode switching */}
						<ViewportManager globalView={globalView} localView={localView} />

						{/* Graph loading experience: full overlay for initial loads and
						    org switches, corner pill for background refetches. z-60 sits
						    above the z-50 floating controls (which shouldn't be usable
						    while the canvas has no data) and below Mantine portals. */}
						{viewState.viewMode === VIEW_MODES.GLOBAL &&
							viewState.globalViewType === GLOBAL_VIEW_TYPES["2D"] && (
								<>
									<GraphLoadingOverlay
										progress={displayedGraphLoadProgress}
										onRetry={handleFetchGraphData}
									/>
									<GraphSyncIndicator visible={isBackgroundRefreshing} />
								</>
							)}

						{/* Floating UI Elements - Only show in global view with the graph mode */}
						{viewState.viewMode === VIEW_MODES.GLOBAL &&
							viewState.globalViewType === GLOBAL_VIEW_TYPES["2D"] && (
								<>
									{isFullscreen ? (
										<>
											<FloatingExitFullscreen
												onExit={() => toggleFullscreen(viewportRef.current)}
											/>
											<FloatingAnimationControls
												isAnimating={bfsAnimation.state.isAnimating}
												isComplete={bfsAnimation.state.isComplete}
												progress={bfsAnimation.state.progress}
												onSkip={bfsAnimation.skipAnimation}
												onReplay={bfsAnimation.replayAnimation}
											/>
										</>
									) : (
										<>
											<FloatingSettings
												showLabels={viewState.showLabels}
												onToggleLabels={viewState.setShowLabels}
												isFullscreen={isFullscreen}
												onToggleFullscreen={() =>
													toggleFullscreen(viewportRef.current)
												}
												fullscreenAnimationEnabled={
													viewState.fullscreenAnimationEnabled
												}
												onToggleFullscreenAnimation={
													viewState.setFullscreenAnimationEnabled
												}
											/>

											<FloatingAddButton
												organizationId={activeOrganization.id}
											/>

											<FloatingWorkflowsButton />

											<FloatingSavaButton onClick={handleToggleSava} />

											{graphData?.nodes && (
												<FloatingPathFinder
													nodes={graphData.nodes}
													selectedNodes={trace.selection}
													paths={trace.paths}
													activePathIndex={trace.activeIndex}
													onPathsFound={handlePathsFound}
													onSelectPath={trace.selectPath}
													onNodeSelectionChange={handleNodeSelectionChange}
												/>
											)}
										</>
									)}

									<FloatingLegend
										graphData={graphData}
										activeFilters={viewState.filters.nodeTypes}
										onFilterToggle={(typeName) => {
											const current = viewState.filters.nodeTypes;
											const newTypes = current.includes(typeName)
												? current.filter((t) => t !== typeName)
												: [...current, typeName];
											viewState.setFilters({ nodeTypes: newTypes });
										}}
										onClearTypeFilters={() =>
											viewState.setFilters({ nodeTypes: [] })
										}
									/>
								</>
							)}
					</div>
				}
				contextPanel={
					<ContextPanel
						graphData={graphData}
						onRefresh={handleFetchGraphData}
					/>
				}
				showSidebar={true}
				showContextPanel={viewState.contextPanelVisible}
			/>

			{/* Sava Sidebar */}
			<SavaSidebar />

			{/* Node detail panel. Hosted here rather than inside <Graph> so Sava's
			    node links can open it from list and duplicates views too. Declared
			    after the Sava drawer: both are right-side drawers at the same
			    z-index, so the one whose portal mounts last wins — and opening a
			    panel from a Sava link should land on top of the conversation, not
			    behind it. */}
			<NodeDetailSidebar
				opened={editNode !== null}
				onClose={resetDetailPanel}
				node={editNode}
				organizationId={activeOrganization.id}
				onSave={handleFetchGraphData}
				onDelete={handleFetchGraphData}
			/>

			{/* Edge timeline for entry points outside the node panel (local views).
			    When the node panel is open it hosts the timeline itself. */}
			<EdgeDetailDrawer
				organizationId={activeOrganization.id}
				onRefresh={handleFetchGraphData}
			/>

			{/* Workflow Components */}
			<WorkflowsSidebar existingNodes={existingNodes} />
			<NaturalLanguageWorkflow
				existingNodes={existingNodes}
				onSave={handleWorkflowSave}
				onComplete={handleFetchGraphData}
				isEmployee={userData?.currentUser?.isEmployee ?? false}
			/>
			<ImageWorkflow
				existingNodes={existingNodes}
				onSave={handleWorkflowSave}
				onComplete={handleFetchGraphData}
				isEmployee={userData?.currentUser?.isEmployee ?? false}
			/>
			<MarkdownWorkflow
				existingNodes={existingNodes}
				onSave={handleWorkflowSave}
				onComplete={handleFetchGraphData}
			/>
			<CsvWorkflow
				existingNodes={existingNodes}
				onSave={handleWorkflowSave}
				onComplete={handleFetchGraphData}
				userNodeId={activeOrganization.nodeId ?? undefined}
			/>
			<VCardWorkflow
				existingNodes={existingNodes}
				onSave={handleWorkflowSave}
				onComplete={handleFetchGraphData}
				userNodeId={activeOrganization.nodeId ?? undefined}
			/>
			<LinkedInWorkflow
				existingNodes={existingNodes}
				existingEdgePairs={existingEdgePairs}
				onSave={handleWorkflowSave}
				onUpdateNodes={handleUpdateNodes}
				onComplete={handleFetchGraphData}
				userNodeId={activeOrganization.nodeId ?? undefined}
			/>
			<ManualWorkflow
				existingNodes={existingNodes}
				onSave={handleWorkflowSave}
				onComplete={handleFetchGraphData}
			/>
		</NodeLinkProvider>
	);
}
